"use client";
import * as React from "react";
import { create } from "zustand";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// 全局 confirm 对话框（替代原生 confirm()，修复长文本挤压不能滚动的问题）
//
// 使用：
//   if (!(await confirmDialog("确定删除？"))) return;
//   if (!(await confirmDialog({ title: "危险操作", description: "...", destructive: true }))) return;
//
// 挂载方式：把 <ConfirmHost /> 放到 Providers 里任何位置。

export interface ConfirmOptions {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}

interface ConfirmState {
  open: boolean;
  options: ConfirmOptions;
  resolver: ((v: boolean) => void) | null;
  show: (opts: ConfirmOptions) => Promise<boolean>;
  close: (v: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  open: false,
  options: {},
  resolver: null,
  show: (opts) =>
    new Promise<boolean>((resolve) => {
      set({ open: true, options: opts, resolver: resolve });
    }),
  close: (v) => {
    const r = get().resolver;
    set({ open: false, resolver: null });
    if (r) r(v);
  },
}));

/** 返回 Promise<boolean>：用户点确定 → true，取消/关闭/ESC → false */
export function confirmDialog(opts: ConfirmOptions | string): Promise<boolean> {
  const options: ConfirmOptions = typeof opts === "string" ? { description: opts } : opts;
  return useConfirmStore.getState().show(options);
}

/** 全局挂载点（放 Providers 里） */
export function ConfirmHost() {
  const open = useConfirmStore((s) => s.open);
  const options = useConfirmStore((s) => s.options);
  const close = useConfirmStore((s) => s.close);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{options.title || "请确认"}</DialogTitle>
          {options.description && (
            <DialogDescription className="whitespace-pre-wrap break-words">
              {options.description}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => close(false)}>
            {options.cancelText || "取消"}
          </Button>
          <Button
            variant={options.destructive ? "destructive" : "default"}
            onClick={() => close(true)}
            autoFocus
          >
            {options.confirmText || "确定"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
